/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext } from "react";
import {
  Search,
  Filter,
  ChevronDown,
  MoreHorizontal,
  Users,
  UserPlus,
  XCircle,
  CheckCircle,
  X,
  RefreshCw,
  Info,
  PlayCircle,
  History,
  Phone,
  Mail,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Pagination from "../../../components/Pagination";
import { AdminContext } from "../../../context/AdminContext";
import axios from "axios";
import { toast } from "react-toastify";
import ActionHistoryModal from "../../../components/ActionHistoryModal";

const titleCase = (str) => {
  if (!str) return "";
  return str
    .split(/[- ]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

const snakeToTitle = (str) => {
  if (!str) return "";
  return str
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join("-");
};

const normalizeWorkState = (state) =>
  typeof state === "string" ? state.replace(/-/g, "_").toLowerCase() : "";

const timeAgo = (dateStr) => {
  if (!dateStr) return "Unknown";
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
};

const ServiceRequests = () => {
  const navigate = useNavigate();
  const {
    getAllJobs,
    closeJob,
    jobs,
    jobsPagination,
    aToken,
    backendUrl,
    serviceCategories,
  } = useContext(AdminContext);

  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [workTimeFilter, setWorkTimeFilter] = useState("All");
  const [subcityFilter, setSubcityFilter] = useState("All");
  const [workStateFilter, setWorkStateFilter] = useState("All");

  // Dropdown states
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showWorkTimeDropdown, setShowWorkTimeDropdown] = useState(false);
  const [showSubcityDropdown, setShowSubcityDropdown] = useState(false);
  const [showWorkStateDropdown, setShowWorkStateDropdown] = useState(false);

  // UI States
  const [activeMenu, setActiveMenu] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [historyModal, setHistoryModal] = useState({
    open: false,
    history: [],
  });

  // Fetch jobs on initial load and when filters/page change
  useEffect(() => {
    fetchJobs();
  }, [
    jobsPagination.page,
    jobsPagination.limit,
    statusFilter,
    categoryFilter,
    workTimeFilter,
    subcityFilter,
    workStateFilter,
  ]);

  // Removed debounced search - search only on Enter/click now

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest(".action-menu")) return;
      setActiveMenu(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setShowStatusDropdown(false);
        setShowCategoryDropdown(false);
        setShowWorkTimeDropdown(false);
        setShowSubcityDropdown(false);
        setShowWorkStateDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      await getAllJobs({
        page: jobsPagination.page,
        limit: jobsPagination.limit,
        search: searchTerm,
        status: statusFilter,
        category: categoryFilter,
        workTime: workTimeFilter,
        subcity: subcityFilter,
        workState: workStateFilter,
      });
    } catch (error) {
      toast.error("Error fetching service requests");
    } finally {
      setLoading(false);
    }
  };

  // Handle search submission
  const handleSearchSubmit = (term = searchInput) => {
    setSearchTerm(term);
    getAllJobs({
      page: 1,
      limit: jobsPagination.limit,
      search: term,
      status: statusFilter,
      category: categoryFilter,
      workTime: workTimeFilter,
      subcity: subcityFilter,
      workState: workStateFilter,
    });
  };

  // Handle Enter key press in search input
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit(searchInput);
    }
  };

  // Fetch job action history
  const fetchJobHistory = async (jobId) => {
    try {
      const response = await axios.get(
        `${backendUrl}/api/admin/jobs/${jobId}/history`,
        { headers: { aToken } },
      );
      if (response.data.success) {
        setHistoryModal({ open: true, history: response.data.history });
      }
    } catch (error) {
      toast.error("Error fetching job history");
      console.error("Error fetching history:", error);
    }
  };

  // Map jobs to request format
  const requests = jobs.map((job) => {
    const workState = normalizeWorkState(job.workState);
    const experienceLevel =
      Number.isInteger(job.experienceMin) && Number.isInteger(job.experienceMax)
        ? `${job.experienceMin}-${job.experienceMax}`
        : Number.isInteger(job.experienceMin)
          ? `${job.experienceMin}+`
          : "";

    return {
      id: job.id,
      description: job.description,
      location: job.location,
      subcity: titleCase(job.subcity),
      experienceLevel,
      category: titleCase(job.serviceCategory?.label || job.category || ""),
      workTime: snakeToTitle(job.workTime),
      status: titleCase(job.status),
      workState,

      // Raw values for logic
      rawStatus: job.status,
      rawWorkState: workState,
      rawCategory: job.serviceCategory?.label || job.category,

      client: {
        id: job.createdBy?.id || job.created_by?.id,
        name: job.createdBy?.name || job.created_by?.name || "Unknown",
        phone:
          job.createdBy?.phoneNumber || job.created_by?.phoneNumber || "N/A",
        email: job.createdBy?.email || job.created_by?.email || "N/A",
        avatar:
          job.createdBy?.image ||
          job.created_by?.image ||
          `https://ui-avatars.com/api/?name=${encodeURIComponent(
            job.createdBy?.name || job.created_by?.name || "Unknown",
          )}&background=random`,
        role: "client",
      },

      datePosted: timeAgo(job.createdAt),
      assignedCaregiver: job.assignedCaregiver,
      fieldValues: job.fieldValues || [],
      createdAt: job.createdAt,
    };
  });

  // Phase-based action logic
  const getRequestPhase = (req) => {
    const workState = req.rawWorkState;
    const status = req.rawStatus;

    // Phase 1: Hiring Phase (Open for applicants)
    const isHiringPhase = status === "open" && workState === "pending";

    // Phase 2: Work Phase (Assigned or in progress)
    const isWorkPhase =
      workState === "assigned" || workState === "work_started";

    // Phase 3: Terminal Phase
    const isTerminalPhase =
      workState === "completed" || workState === "cancelled";

    // Can close (reject all applicants) only in hiring phase
    const canCloseRequest = isHiringPhase;

    // Can assign caregiver only in hiring phase
    const canAssignCaregiver = isHiringPhase;

    // Can start work only when assigned (transition to work-started)
    const canStartWork = workState === "assigned";

    // Can complete only if work-started
    const canComplete = workState === "work_started";

    // Can cancel in work phase (assigned or work-started)
    const canCancel = isWorkPhase;

    return {
      isHiringPhase,
      isWorkPhase,
      isTerminalPhase,
      canCloseRequest,
      canAssignCaregiver,
      canStartWork,
      canComplete,
      canCancel,
      canViewOnly: isTerminalPhase,
    };
  };

  const handlePageChange = (page) => {
    getAllJobs({
      page,
      limit: jobsPagination.limit,
      search: searchTerm,
      status: statusFilter,
      category: categoryFilter,
      workTime: workTimeFilter,
      subcity: subcityFilter,
      workState: workStateFilter,
    });
  };

  const handleItemsPerPageChange = (limit) => {
    getAllJobs({
      page: 1,
      limit,
      search: searchTerm,
      status: statusFilter,
      category: categoryFilter,
      workTime: workTimeFilter,
      subcity: subcityFilter,
      workState: workStateFilter,
    });
  };

  const handleResetFilters = () => {
    setSearchInput("");
    setSearchTerm("");
    setStatusFilter("All");
    setCategoryFilter("All");
    setWorkTimeFilter("All");
    setSubcityFilter("All");
    setWorkStateFilter("All");

    getAllJobs({
      page: 1,
      limit: jobsPagination.limit,
      search: "",
      status: "All",
      category: "All",
      workTime: "All",
      subcity: "All",
      workState: "All",
    });
  };

  const handleFilterChange = (filterType, value) => {
    // Reset to page 1 when any filter changes
    const newParams = {
      page: 1,
      limit: jobsPagination.limit,
      search: searchTerm,
    };

    switch (filterType) {
      case "status":
        setStatusFilter(value);
        newParams.status = value;
        newParams.category = categoryFilter;
        newParams.workTime = workTimeFilter;
        newParams.subcity = subcityFilter;
        newParams.workState = workStateFilter;
        break;
      case "category":
        setCategoryFilter(value);
        newParams.status = statusFilter;
        newParams.category = value;
        newParams.workTime = workTimeFilter;
        newParams.subcity = subcityFilter;
        newParams.workState = workStateFilter;
        break;
      case "workTime":
        setWorkTimeFilter(value);
        newParams.status = statusFilter;
        newParams.category = categoryFilter;
        newParams.workTime = value;
        newParams.subcity = subcityFilter;
        newParams.workState = workStateFilter;
        break;
      case "subcity":
        setSubcityFilter(value);
        newParams.status = statusFilter;
        newParams.category = categoryFilter;
        newParams.workTime = workTimeFilter;
        newParams.subcity = value;
        newParams.workState = workStateFilter;
        break;
      case "workState":
        setWorkStateFilter(value);
        newParams.status = statusFilter;
        newParams.category = categoryFilter;
        newParams.workTime = workTimeFilter;
        newParams.subcity = subcityFilter;
        newParams.workState = value;
        break;
      default:
        break;
    }

    getAllJobs(newParams);
  };

  // Start Work handler using axios directly
  const handleStartWork = async (id) => {
    try {
      const { data } = await axios.patch(
        `${backendUrl}/api/admin/jobs/start-work`,
        { jobId: id },
        { headers: { aToken } },
      );
      toast.success(data.message || "Work started successfully");
      await fetchJobs();
      setShowActionModal(null);
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      toast.error(message);
    }
  };

  const handleStatusUpdate = async (id, field, value) => {
    try {
      if (field === "status" && value === "Closed") {
        // Hiring phase: Close request (reject all pending applications)
        await closeJob(id);
        toast.success(
          "Request closed and cancelled successfully. All applicants rejected.",
        );
      } else if (field === "workState" && value === "Completed") {
        // Work phase: Mark as completed (frees caregiver)
        const { data } = await axios.post(
          `${backendUrl}/api/admin/jobs/complete`,
          { jobId: id },
          { headers: { aToken } },
        );
        toast.success(data.message || "Job completed successfully");
      } else if (field === "workState" && value === "Cancelled") {
        // Work phase: Cancel job (frees caregiver)
        const { data } = await axios.post(
          `${backendUrl}/api/admin/jobs/cancel`,
          { jobId: id },
          { headers: { aToken } },
        );
        toast.success(data.message || "Job cancelled successfully");
      }

      // Refresh with current filters - this is the correct place to refresh
      await fetchJobs();
      setShowActionModal(null);
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      toast.error(message);
    }
  };

  const getWorkStateColor = (state) => {
    switch (state?.toLowerCase()) {
      case "pending":
        return "bg-[#DDDCDA] text-gray-800 border-gray-400 dark:bg-[#DDDCDA] dark:text-gray-800 dark:border-gray-400";
      case "assigned":
        return "bg-blue-600 text-white border-blue-700 dark:bg-blue-700 dark:text-white dark:border-blue-800";
      case "work_started":
        return "bg-gray-600 text-white border-gray-700 dark:bg-gray-700 dark:text-white dark:border-gray-800";
      case "completed":
        return "bg-emerald-600 text-white border-emerald-700 dark:bg-emerald-700 dark:text-white dark:border-emerald-800";
      case "cancelled":
        return "bg-red-600 text-white border-red-700 dark:bg-red-700 dark:text-white dark:border-red-800";
      default:
        return "bg-gray-500 text-white border-gray-600 dark:bg-gray-700 dark:text-white dark:border-gray-800";
    }
  };

  const getWorkStateLabel = (state) => {
    const labels = {
      pending: "Pending",
      assigned: "Assigned",
      work_started: "Work Started",
      completed: "Completed",
      cancelled: "Cancelled",
    };
    return labels[state] || state;
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-text dark:text-dark-text">
            Service Requests
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1">
            Manage jobs, assign caregivers, and track work states.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500 dark:text-dark-muted">
            Total:{" "}
            <span className="font-semibold text-text dark:text-dark-text">
              {jobsPagination.total}
            </span>{" "}
            requests
          </div>
        </div>
      </div>

      {/* Modern Filter Section - Matching BookingsTable Style */}
      <div className="bg-white dark:bg-dark-surface p-4 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border mb-6">
        {/* Top Row: Search & Toggles */}
        <div className="flex flex-col md:flex-row gap-4 justify-between items-center mb-2">
          <div className="relative w-full md:flex-1">
            <div className="absolute left-4 top-1/2 -translate-y-1/2">
              <Search
                className="text-gray-400 cursor-pointer hover:text-gray-600 transition-colors"
                size={20}
                onClick={() => handleSearchSubmit()}
              />
            </div>
            <input
              type="text"
              placeholder="Search by description, location..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full pl-12 pr-4 py-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-dark-border outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700 text-text dark:text-dark-text placeholder-gray-400 dark:placeholder-gray-500 font-medium transition-all"
            />
            {searchInput && (
              <button
                onClick={() => {
                  setSearchInput("");
                  handleSearchSubmit("");
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X size={18} />
              </button>
            )}
            <div className="absolute right-12 top-1/2 -translate-y-1/2 text-xs text-gray-400 hidden md:block">
              Press Enter to search
            </div>
          </div>
          <div className="flex gap-3 w-full md:w-auto">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all border ${
                showFilters
                  ? "bg-gray-100 text-text dark:bg-gray-800 dark:text-dark-text border-gray-300 dark:border-gray-600"
                  : "bg-white text-gray-600 dark:bg-dark-surface dark:text-dark-muted border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
            >
              <Filter size={18} />
              <span>{showFilters ? "Hide Filters" : "Show Filters"}</span>
            </button>
            {showFilters && (
              <button
                onClick={handleResetFilters}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700"
                title="Reset Filters"
              >
                <RefreshCw size={18} />
              </button>
            )}
          </div>
        </div>

        {/* Collapsible Filters  */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 overflow-visible transition-all duration-300 ease-in-out ${
            showFilters
              ? "max-h-[600px] opacity-100 mt-4 pt-4 border-t border-gray-200 dark:border-dark-border"
              : "max-h-0 opacity-0"
          }`}
        >
          {/* Status Filter */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider ml-1">
              Job Status
            </label>
            <div className="relative dropdown-container">
              <button
                onClick={() => {
                  setShowStatusDropdown(!showStatusDropdown);
                  setShowCategoryDropdown(false);
                  setShowWorkTimeDropdown(false);
                  setShowSubcityDropdown(false);
                  setShowWorkStateDropdown(false);
                }}
                className="w-full flex items-center justify-between pl-4 pr-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-dark-border text-sm font-medium text-text dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <span>
                  {statusFilter === "All"
                    ? "All Statuses"
                    : statusFilter === "Open"
                      ? "Open (Accepting)"
                      : "Closed (Not Accepting)"}
                </span>
                <ChevronDown
                  size={14}
                  className={`text-primary transition-transform ${
                    showStatusDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showStatusDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 animate-in fade-in zoom-in-95 duration-200">
                  {[
                    { value: "All", label: "All Statuses" },
                    { value: "Open", label: "Open (Accepting)" },
                    { value: "Closed", label: "Closed (Not Accepting)" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        handleFilterChange("status", option.value);
                        setShowStatusDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        statusFilter === option.value
                          ? "bg-primary text-white dark:bg-primary"
                          : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Category Filter */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider ml-1">
              Category
            </label>
            <div className="relative dropdown-container">
              <button
                onClick={() => {
                  setShowCategoryDropdown(!showCategoryDropdown);
                  setShowStatusDropdown(false);
                  setShowWorkTimeDropdown(false);
                  setShowSubcityDropdown(false);
                  setShowWorkStateDropdown(false);
                }}
                className="w-full flex items-center justify-between pl-4 pr-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-dark-border text-sm font-medium text-text dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <span>
                  {categoryFilter === "All"
                    ? "All Categories"
                    : serviceCategories.find((c) => c.id === categoryFilter)
                        ?.label || categoryFilter}
                </span>
                <ChevronDown
                  size={14}
                  className={`text-primary transition-transform ${
                    showCategoryDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showCategoryDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                  <button
                    onClick={() => {
                      handleFilterChange("category", "All");
                      setShowCategoryDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      categoryFilter === "All"
                        ? "bg-primary text-white dark:bg-primary"
                        : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                    }`}
                  >
                    All Categories
                  </button>
                  {serviceCategories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => {
                        handleFilterChange("category", cat.id);
                        setShowCategoryDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        categoryFilter === cat.id
                          ? "bg-primary text-white dark:bg-primary"
                          : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Work Time Filter  */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider ml-1">
              Work Schedule
            </label>
            <div className="relative dropdown-container">
              <button
                onClick={() => {
                  setShowWorkTimeDropdown(!showWorkTimeDropdown);
                  setShowStatusDropdown(false);
                  setShowCategoryDropdown(false);
                  setShowSubcityDropdown(false);
                  setShowWorkStateDropdown(false);
                }}
                className="w-full flex items-center justify-between pl-4 pr-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-dark-border text-sm font-medium text-text dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <span>
                  {workTimeFilter === "All" ? "All Schedules" : workTimeFilter}
                </span>
                <ChevronDown
                  size={14}
                  className={`text-primary transition-transform ${
                    showWorkTimeDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showWorkTimeDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 animate-in fade-in zoom-in-95 duration-200">
                  {[
                    { value: "All", label: "All Schedules" },
                    { value: "Part-Time", label: "Part-Time" },
                    { value: "On-Demand", label: "On-Demand" },
                    { value: "Living-In", label: "Living-In" },
                    { value: "Night-Shift", label: "Night-Shift" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        handleFilterChange("workTime", option.value);
                        setShowWorkTimeDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        workTimeFilter === option.value
                          ? "bg-primary text-white dark:bg-primary"
                          : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Subcity Filter */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider ml-1">
              Location (Subcity)
            </label>
            <div className="relative dropdown-container">
              <button
                onClick={() => {
                  setShowSubcityDropdown(!showSubcityDropdown);
                  setShowStatusDropdown(false);
                  setShowCategoryDropdown(false);
                  setShowWorkTimeDropdown(false);
                  setShowWorkStateDropdown(false);
                }}
                className="w-full flex items-center justify-between pl-4 pr-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-dark-border text-sm font-medium text-text dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <span>
                  {subcityFilter === "All" ? "All Subcities" : subcityFilter}
                </span>
                <ChevronDown
                  size={14}
                  className={`text-primary transition-transform ${
                    showSubcityDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showSubcityDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                  <button
                    onClick={() => {
                      handleFilterChange("subcity", "All");
                      setShowSubcityDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      subcityFilter === "All"
                        ? "bg-primary text-white dark:bg-primary"
                        : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                    }`}
                  >
                    All Subcities
                  </button>
                  {[
                    "Addis Ketema",
                    "Akaki Kaliti",
                    "Arada",
                    "Bole",
                    "Gullele",
                    "Kirkos",
                    "Kolfe Keranio",
                    "Lideta",
                    "Nifas Silk-Lafto",
                    "Yeka",
                    "Lemi Kura",
                  ].map((subcity) => (
                    <button
                      key={subcity}
                      onClick={() => {
                        handleFilterChange("subcity", subcity);
                        setShowSubcityDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        subcityFilter === subcity
                          ? "bg-primary text-white dark:bg-primary"
                          : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                      }`}
                    >
                      {subcity}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Work State Filter */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider ml-1">
              Work State
            </label>
            <div className="relative dropdown-container">
              <button
                onClick={() => {
                  setShowWorkStateDropdown(!showWorkStateDropdown);
                  setShowStatusDropdown(false);
                  setShowCategoryDropdown(false);
                  setShowWorkTimeDropdown(false);
                  setShowSubcityDropdown(false);
                }}
                className="w-full flex items-center justify-between pl-4 pr-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-dark-border text-sm font-medium text-text dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <span>
                  {workStateFilter === "All"
                    ? "All States"
                    : workStateFilter === "pending"
                      ? "Pending (Hiring)"
                      : workStateFilter === "assigned"
                        ? "Assigned"
                        : workStateFilter === "work_started"
                          ? "Work Started"
                          : workStateFilter === "completed"
                            ? "Completed"
                            : workStateFilter === "cancelled"
                              ? "Cancelled"
                              : workStateFilter}
                </span>
                <ChevronDown
                  size={14}
                  className={`text-primary transition-transform ${
                    showWorkStateDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showWorkStateDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 animate-in fade-in zoom-in-95 duration-200">
                  {[
                    { value: "All", label: "All States" },
                    { value: "pending", label: "Pending (Hiring)" },
                    { value: "assigned", label: "Assigned" },
                    { value: "work_started", label: "Work Started" },
                    { value: "completed", label: "Completed" },
                    { value: "cancelled", label: "Cancelled" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        handleFilterChange("workState", option.value);
                        setShowWorkStateDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        workStateFilter === option.value
                          ? "bg-primary text-white dark:bg-primary"
                          : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-visible">
        <div className="overflow-visible min-h-[400px]">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-500 dark:text-dark-muted uppercase bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-4 font-bold tracking-wider">
                  Description & Phase
                </th>
                <th className="px-6 py-4 font-bold tracking-wider">Location</th>
                <th className="px-6 py-4 font-bold tracking-wider">
                  Experience
                </th>
                <th className="px-6 py-4 font-bold tracking-wider">Category</th>
                <th className="px-6 py-4 font-bold tracking-wider">Schedule</th>
                <th className="px-6 py-4 font-bold tracking-wider">Status</th>
                <th className="px-6 py-4 font-bold tracking-wider">
                  Work State
                </th>
                <th className="px-6 py-4 font-bold tracking-wider">Client</th>
                <th className="px-6 py-4 font-bold tracking-wider text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 dark:border-gray-400"></div>
                    </div>
                  </td>
                </tr>
              ) : requests.length > 0 ? (
                requests.map((req) => {
                  const phase = getRequestPhase(req);
                  const phaseLabel = phase.isHiringPhase
                    ? "Hiring Phase"
                    : phase.isWorkPhase
                      ? "Active Work"
                      : phase.isTerminalPhase
                        ? "Finished"
                        : "Unknown";

                  return (
                    <tr
                      key={req.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
                    >
                      <td
                        className="px-6 py-4 cursor-pointer"
                        onClick={() => {
                          setSelectedRequest(req);
                          setShowDetailModal(true);
                        }}
                      >
                        <div className="font-medium text-text dark:text-dark-text max-w-xs truncate hover:underline">
                          {req.description}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-dark-muted mt-1 flex items-center gap-1">
                          <span
                            className={`w-2 h-2 rounded-full ${
                              phase.isHiringPhase
                                ? "bg-amber-400"
                                : phase.isWorkPhase
                                  ? "bg-emerald-400"
                                  : "bg-gray-400"
                            }`}
                          ></span>
                          {phaseLabel}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-text dark:text-dark-text font-medium">
                            {req.subcity}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-dark-muted truncate max-w-[150px]">
                            {req.location}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600 dark:text-dark-muted">
                        {req.experienceLevel || "Any"}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs font-semibold border border-gray-200 dark:border-gray-700">
                          {req.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600 dark:text-dark-muted">
                        {req.workTime}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                            req.rawStatus === "open"
                              ? "bg-emerald-600 text-white border-emerald-700 dark:bg-emerald-700 dark:text-white dark:border-emerald-800"
                              : "bg-red-600 text-white border-red-700 dark:bg-red-700 dark:text-white dark:border-red-800"
                          }`}
                        >
                          {req.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getWorkStateColor(
                            req.rawWorkState,
                          )}`}
                        >
                          {getWorkStateLabel(req.rawWorkState)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                            <img
                              src={req.client.avatar}
                              alt={req.client.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-text dark:text-dark-text font-medium text-xs">
                              {req.client.name}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-dark-muted">
                              {req.client.phone}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center relative action-menu">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveMenu(
                              activeMenu === req.id ? null : req.id,
                            );
                          }}
                          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-500 dark:text-dark-muted hover:text-text dark:hover:text-dark-text transition-colors"
                        >
                          <MoreHorizontal size={18} />
                        </button>

                        {/* Action Dropdown */}
                        {activeMenu === req.id && (
                          <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-dark-surface rounded-lg shadow-lg border border-gray-200 dark:border-dark-border z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 py-1">
                            {/* View Details  */}
                            <button
                              onClick={() => {
                                setSelectedRequest(req);
                                setShowDetailModal(true);
                                setActiveMenu(null);
                              }}
                              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 transition-colors"
                            >
                              <Info
                                size={16}
                                className="text-gray-500 dark:text-gray-400"
                              />
                              <span>View Details</span>
                            </button>

                            {/* View Applicants */}
                            <button
                              onClick={() => {
                                navigate(`/requests/${req.id}/applicants`);
                                setActiveMenu(null);
                              }}
                              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 transition-colors"
                            >
                              <Users
                                size={16}
                                className="text-gray-500 dark:text-gray-400"
                              />
                              <span>View Applicants</span>
                            </button>

                            {/* View Action History */}
                            <button
                              onClick={() => {
                                fetchJobHistory(req.id);
                                setActiveMenu(null);
                              }}
                              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 transition-colors"
                            >
                              <History
                                size={16}
                                className="text-gray-500 dark:text-gray-400"
                              />
                              <span>View Action History</span>
                            </button>

                            {/* Phase 1: Hiring Phase Actions */}
                            {phase.canAssignCaregiver && (
                              <button
                                onClick={() => {
                                  navigate(`/requests/${req.id}/assign`);
                                  setActiveMenu(null);
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 transition-colors"
                              >
                                <UserPlus
                                  size={16}
                                  className="text-gray-500 dark:text-gray-400"
                                />
                                <span>Assign Caregiver Directly</span>
                              </button>
                            )}

                            {phase.canCloseRequest && (
                              <>
                                <div className="h-px bg-gray-100 dark:bg-gray-700 my-1"></div>
                                <button
                                  onClick={() => {
                                    setShowActionModal({ type: "close", req });
                                    setActiveMenu(null);
                                  }}
                                  className="w-full text-left px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 transition-colors"
                                >
                                  <XCircle size={16} />
                                  <span>Close Request (Reject All)</span>
                                </button>
                              </>
                            )}

                            {/* Phase 2: Work Phase Actions */}
                            {phase.isWorkPhase && (
                              <>
                                <div className="h-px bg-gray-100 dark:bg-gray-700 my-1"></div>

                                {/* Start Work - Only when assigned */}
                                {phase.canStartWork && (
                                  <button
                                    onClick={() => {
                                      setShowActionModal({
                                        type: "start",
                                        req,
                                      });
                                      setActiveMenu(null);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center gap-2 transition-colors"
                                  >
                                    <PlayCircle size={16} />
                                    <span>Start Work</span>
                                  </button>
                                )}

                                {/* Complete Job - Only when work-started */}
                                {phase.canComplete && (
                                  <button
                                    onClick={() => {
                                      setShowActionModal({
                                        type: "complete",
                                        req,
                                      });
                                      setActiveMenu(null);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-sm text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 flex items-center gap-2 transition-colors"
                                  >
                                    <CheckCircle size={16} />
                                    <span>Complete Job</span>
                                  </button>
                                )}

                                {phase.canCancel && (
                                  <button
                                    onClick={() => {
                                      setShowActionModal({
                                        type: "cancel",
                                        req,
                                      });
                                      setActiveMenu(null);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 transition-colors"
                                  >
                                    <XCircle size={16} />
                                    <span>Cancel Job (Free Caregiver)</span>
                                  </button>
                                )}
                              </>
                            )}

                            {/* Phase 3: Terminal Phase - Info only */}
                            {phase.canViewOnly && (
                              <div className="px-4 py-3 text-xs text-gray-500 dark:text-dark-muted italic text-center bg-gray-50 dark:bg-gray-900/50">
                                No actions available - Job is{" "}
                                {req.workState.toLowerCase()}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={9}
                    className="px-6 py-12 text-center text-gray-500 dark:text-dark-muted"
                  >
                    {searchTerm
                      ? `No results found for "${searchTerm}"`
                      : "No service requests found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="border-t border-gray-200 dark:border-dark-border bg-gray-50/50 dark:bg-gray-900/50 p-2">
          <Pagination
            currentPage={jobsPagination.page}
            totalPages={jobsPagination.totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={jobsPagination.limit}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={jobsPagination.total}
          />
        </div>
      </div>

      {/* Details Modal */}
      {showDetailModal && selectedRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200 border border-gray-200 dark:border-dark-border">
            <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200 dark:border-dark-border flex justify-between items-center sticky top-0 bg-white dark:bg-dark-surface z-10">
              <h2 className="text-lg sm:text-xl font-bold text-text dark:text-dark-text">
                Request Details
              </h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X size={22} />
              </button>
            </div>
            <div className="p-4 sm:p-6 md:p-8">
              {/* Phase Badge */}
              <div className="mb-6">
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${
                    selectedRequest.rawWorkState === "pending"
                      ? "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800"
                      : selectedRequest.rawWorkState === "assigned"
                        ? "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800"
                        : selectedRequest.rawWorkState === "work_started"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800"
                          : "bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600"
                  }`}
                >
                  {getRequestPhase(selectedRequest).isHiringPhase
                    ? "Hiring Phase"
                    : getRequestPhase(selectedRequest).isWorkPhase
                      ? "Active Work"
                      : "Completed"}
                </span>
              </div>

              <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-8">
                <img
                  src={selectedRequest.client.avatar}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-4 border-gray-100 dark:border-gray-700"
                  alt={selectedRequest.client.name}
                />
                <div className="min-w-0 w-full">
                  <h3 className="text-lg sm:text-xl font-bold text-text dark:text-dark-text break-words">
                    {selectedRequest.client.name}
                  </h3>
                  <div className="space-y-1 mt-2">
                    <p className="flex items-center gap-1.5 text-gray-500 dark:text-dark-muted break-all">
                      <Phone size={14} /> {selectedRequest.client.phone}
                    </p>
                    <p className="flex items-center gap-1.5 text-gray-500 dark:text-dark-muted break-all">
                      <Mail size={14} /> {selectedRequest.client.email}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-dark-muted">
                      Role: {selectedRequest.client.role}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className="px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-xs font-bold text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                      {selectedRequest.subcity}
                    </span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 rounded-full text-xs font-bold border border-gray-200 dark:border-gray-700">
                      {selectedRequest.datePosted}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-900/50 p-6 rounded-xl mb-6 border border-gray-200 dark:border-dark-border">
                <h4 className="text-sm font-bold text-gray-400 dark:text-dark-muted uppercase mb-2">
                  Job Description
                </h4>
                <p className="text-text dark:text-dark-text text-base sm:text-lg font-medium break-words">
                  {selectedRequest.description}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 border border-gray-200 dark:border-dark-border rounded-xl bg-white dark:bg-dark-surface">
                  <span className="text-xs text-gray-400 dark:text-dark-muted uppercase font-bold">
                    Category
                  </span>
                  <p className="font-bold text-text dark:text-dark-text break-words">
                    {selectedRequest.category}
                  </p>
                </div>
                <div className="p-4 border border-gray-200 dark:border-dark-border rounded-xl bg-white dark:bg-dark-surface">
                  <span className="text-xs text-gray-400 dark:text-dark-muted uppercase font-bold">
                    Work Time
                  </span>
                  <p className="font-bold text-text dark:text-dark-text break-words">
                    {selectedRequest.workTime}
                  </p>
                </div>
                <div className="p-4 border border-gray-200 dark:border-dark-border rounded-xl bg-white dark:bg-dark-surface">
                  <span className="text-xs text-gray-400 dark:text-dark-muted uppercase font-bold">
                    Experience Required
                  </span>
                  <p className="font-bold text-text dark:text-dark-text">
                    {selectedRequest.experienceLevel || "Not specified"}
                  </p>
                </div>
                <div className="p-4 border border-gray-200 dark:border-dark-border rounded-xl bg-white dark:bg-dark-surface">
                  <span className="text-xs text-gray-400 dark:text-dark-muted uppercase font-bold">
                    Applications
                  </span>
                  <p className="font-bold text-gray-600 dark:text-gray-400">
                    View list for details
                  </p>
                </div>
              </div>

              {/* Category-Specific Field Values */}
              {selectedRequest.fieldValues &&
                selectedRequest.fieldValues.length > 0 && (
                  <div className="mt-6 bg-gray-50 dark:bg-gray-900/50 p-5 rounded-xl border border-gray-200 dark:border-dark-border">
                    <h4 className="text-sm font-bold text-gray-400 dark:text-dark-muted uppercase mb-3">
                      Category-Specific Requirements
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {selectedRequest.fieldValues.map((fv) => {
                        let vals = [];
                        if (Array.isArray(fv.values)) {
                          vals = fv.values;
                        } else if (typeof fv.values === "string") {
                          try {
                            vals = JSON.parse(fv.values);
                          } catch {
                            vals = [fv.values];
                          }
                        }
                        return (
                          <div
                            key={fv.id || fv.fieldId}
                            className="p-3 bg-white dark:bg-dark-surface rounded-lg border border-gray-100 dark:border-dark-border"
                          >
                            <span className="text-xs text-gray-400 dark:text-dark-muted uppercase block mb-1">
                              {fv.field?.label || fv.fieldId}
                            </span>
                            <p className="font-semibold text-sm text-gray-800 dark:text-gray-200 break-words">
                              {vals.join(", ") || "—"}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              {selectedRequest.assignedCaregiver ? (
                <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-dark-border">
                  <h4 className="text-sm font-bold text-gray-400 dark:text-dark-muted uppercase mb-2">
                    Assigned Caregiver
                  </h4>
                  <p className="font-bold text-text dark:text-dark-text">
                    {selectedRequest.assignedCaregiver.name || "Assigned"}
                  </p>
                  <p className="flex items-center gap-1.5 text-gray-500 dark:text-dark-muted text-sm">
                    <Phone size={14} />{" "}
                    {selectedRequest.assignedCaregiver.phone || "N/A"}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-dark-muted mt-1">
                    Caregiver ID:{" "}
                    {selectedRequest.assignedCaregiver?.id ||
                      selectedRequest.assignedCaregiver}
                  </p>
                </div>
              ) : (
                <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-dark-border">
                  <h4 className="text-sm font-bold text-gray-400 dark:text-dark-muted uppercase mb-2">
                    Assignment Status
                  </h4>
                  <p className="font-medium text-gray-600 dark:text-gray-400">
                    No caregiver assigned yet
                  </p>
                </div>
              )}

              <div className="mt-4 text-xs text-gray-400 dark:text-dark-muted">
                Request ID: {selectedRequest.id}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Confirmation Modal */}
      {showActionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200 p-6 text-center border border-gray-200 dark:border-dark-border">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
                showActionModal.type === "complete"
                  ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"
                  : showActionModal.type === "start"
                    ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                    : "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
              }`}
            >
              {showActionModal.type === "complete" ? (
                <CheckCircle size={24} />
              ) : showActionModal.type === "start" ? (
                <PlayCircle size={24} />
              ) : (
                <XCircle size={24} />
              )}
            </div>
            <h3 className="text-xl font-bold mb-2 text-text dark:text-dark-text">
              {showActionModal.type === "close"
                ? "Close Request?"
                : showActionModal.type === "cancel"
                  ? "Cancel Active Job?"
                  : showActionModal.type === "start"
                    ? "Start Work?"
                    : "Complete Job?"}
            </h3>
            <p className="text-gray-500 dark:text-dark-muted mb-6">
              {showActionModal.type === "close"
                ? "This will reject all pending applicants and close the request. No new applications will be accepted."
                : showActionModal.type === "cancel"
                  ? "This will free the assigned caregiver and mark the job as cancelled. This action cannot be undone."
                  : showActionModal.type === "start"
                    ? "Mark this job as actively in progress? The caregiver should begin working now."
                    : "Confirm that the caregiver has successfully completed all work. The caregiver will be freed for new assignments."}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowActionModal(null)}
                className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-200 dark:border-dark-border"
              >
                Back
              </button>
              <button
                onClick={() => {
                  if (showActionModal.type === "start") {
                    handleStartWork(showActionModal.req.id);
                  } else {
                    handleStatusUpdate(
                      showActionModal.req.id,
                      showActionModal.type === "close" ? "status" : "workState",
                      showActionModal.type === "close"
                        ? "Closed"
                        : showActionModal.type === "complete"
                          ? "Completed"
                          : "Cancelled",
                    );
                  }
                }}
                className={`flex-1 px-4 py-2 text-white rounded-lg font-medium ${
                  showActionModal.type === "complete"
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : showActionModal.type === "start"
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-red-600 hover:bg-red-700"
                }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action History Modal */}
      <ActionHistoryModal
        isOpen={historyModal.open}
        onClose={() => setHistoryModal({ open: false, history: [] })}
        history={historyModal.history}
      />
    </div>
  );
};

export default ServiceRequests;
